import { FieldType } from "@dto/fields";
import { FieldColumn, getSrcField } from "@dto/records";
import { CHECKBOX_COLUMN_MIN_WIDTH, MIN_COLUMN_WIDTH } from "@utils/constants";
import { ColumnSizingState } from "@tanstack/react-table";

// Minimum width a column can be resized to. A checkbox column renders a
// fixed-size icon that must never shrink, so its floor is the icon width plus
// the cell padding rather than the default floor.
export function getColumnMinSize(fieldColumn: FieldColumn): number {
    return getSrcField(fieldColumn).type === FieldType.Boolean ? CHECKBOX_COLUMN_MIN_WIDTH : MIN_COLUMN_WIDTH;
}

// Above the z-index of the body cells that scroll under the header (see
// getColumnStyle in DataTable).
const HEADER_BASE_Z_INDEX = 20;

// headerZIndex is the stacking order of a header cell. A cell stacks above the
// next one, so that its resize handle, centered on the column boundary, can
// overflow on its right neighbour instead of being covered by it. The frozen
// columns stack above all the others, which scroll under them.
export function headerZIndex(index: number, count: number, isPinned: boolean): number {
    const level = isPinned ? 2 * count : count;
    return HEADER_BASE_Z_INDEX + level - index;
}

// Persistence of the grid column widths.
//
// TanStack keys its column sizing state by column id, and the grid builds the
// ids from the column POSITION (`<pathKey>-<index>`, see columnId). Persisting
// that state as is ties every width to a position: hiding or showing a column
// before another shifts the ids, and the widths land on the wrong columns (a
// pivot then gets one default-sized column while the browser's fixed table
// layout spreads the spanning pivot header over its columns — the summaries
// positioned from the column sizes drift away from the rendered cells).
//
// The widths are therefore persisted under a STABLE key (columnSizingKey):
// the path key of the column, shared by all the columns of a pivot value —
// the pivot columns always render at the same width. The TanStack state is
// derived from these sizes for the current columns (toTableColumnSizing) and
// a resize is folded back into them (applyTableColumnSizing).

export type ColumnSizes = Record<string, number>;

const PIVOT_SIZING_SUFFIX = "#pivot";
const LEGACY_KEY_PATTERN = /-\d+$/;

// columnId is the TanStack id of the column displaying the fieldColumn at the
// given index.
export function columnId(pathKeys: (string | null)[], index: number): string {
    return pathKeys[index] + "-" + index;
}

// columnSizingKey is the key under which the width of a column is persisted:
// the path key, suffixed for the pivot columns (which share their value path
// key, and one width).
export function columnSizingKey(fieldColumn: FieldColumn): string {
    const base = fieldColumn.pathKey ?? fieldColumn.field._key;
    return fieldColumn.isPivot ? base + PIVOT_SIZING_SUFFIX : base;
}

// toTableColumnSizing derives the TanStack column sizing state (keyed by
// column id) from the persisted sizes for the displayed columns; the columns
// without a persisted width keep the default size.
export function toTableColumnSizing(sizes: ColumnSizes, fieldColumns: FieldColumn[], pathKeys: (string | null)[]): ColumnSizingState {
    const state: ColumnSizingState = {};
    fieldColumns.forEach((fieldColumn, index) => {
        const size = sizes[columnSizingKey(fieldColumn)];
        if (size !== undefined) {
            state[columnId(pathKeys, index)] = size;
        }
    });
    return state;
}

// applyTableColumnSizing folds a TanStack sizing change back into the
// persisted sizes: the columns whose width changed between the current and
// the next state update their stable key (so resizing one pivot column
// resizes all of them). The technical columns (selection, group shift) are
// not persisted.
export function applyTableColumnSizing(sizes: ColumnSizes, current: ColumnSizingState, next: ColumnSizingState, fieldColumns: FieldColumn[], pathKeys: (string | null)[]): ColumnSizes {
    const result: ColumnSizes = { ...sizes };
    fieldColumns.forEach((fieldColumn, index) => {
        const id = columnId(pathKeys, index);
        const size = next[id];
        if (size !== undefined && size !== current[id]) {
            result[columnSizingKey(fieldColumn)] = size;
        }
    });
    return result;
}

// migrateLegacyColumnSizing converts a persisted state still keyed by column
// id (`<pathKey>-<index>`) to stable keys, using the currently displayed
// columns to resolve the ids: an entry is kept when a column with that id is
// displayed (the entries of hidden columns are dropped). Already stable
// entries win over legacy ones. Returns the input untouched when nothing is
// legacy.
export function migrateLegacyColumnSizing(stored: ColumnSizes, fieldColumns: FieldColumn[], pathKeys: (string | null)[]): { migrated: boolean, sizes: ColumnSizes } {
    const sizes: ColumnSizes = {};
    const legacy = new Map<string, number>();
    Object.entries(stored).forEach(([key, size]) => {
        if (LEGACY_KEY_PATTERN.test(key)) {
            legacy.set(key, size);
        } else {
            sizes[key] = size;
        }
    });
    if (legacy.size === 0) {
        return { migrated: false, sizes: stored };
    }
    fieldColumns.forEach((fieldColumn, index) => {
        const stableKey = columnSizingKey(fieldColumn);
        if (sizes[stableKey] !== undefined) {
            return;
        }
        const size = legacy.get(columnId(pathKeys, index));
        if (size !== undefined) {
            sizes[stableKey] = size;
        }
    });
    return { migrated: true, sizes };
}
